/**
 * スケジュール関連のユーティリティ関数
 */
import type { Case } from '../types';
import { SCHEDULE_CONSTANTS, CASE_TYPE_LABELS } from '../constants/dashboard';

/**
 * 案件タイプ（陸送員向けの分類）
 */
export type CaseType = 'pickup' | 'delivery' | 'transit';

/**
 * 時間情報
 */
export interface TimeInfo {
  startTime: string;
  endTime: string;
  label: string;
}

/**
 * 案件タイプを判定（陸送員向け）
 * @param case_ 案件データ
 * @param selectedDate 選択された日付
 * @returns 案件タイプ
 */
export function getCaseType(case_: Case, selectedDate: string): CaseType {
  if (case_.pickupDate === selectedDate) {
    if (case_.deliveryDate === selectedDate) {
      return 'pickup'; // 同日引取・搬入
    }
    return 'pickup'; // 引取日
  } else if (case_.deliveryDate === selectedDate) {
    return 'delivery'; // 搬入日
  }
  // どちらにも一致しない場合は「移送中」とみなす（pickupDate/deliveryDateが未定義の場合も含む）
  return 'transit'; // 移送中
}

/**
 * 案件の時間情報を取得
 * @param case_ 案件データ
 * @param selectedDate 選択された日付
 * @returns 時間情報
 */
export function getTimeInfo(case_: Case, selectedDate: string): TimeInfo {
  const type = getCaseType(case_, selectedDate);

  switch (type) {
    case 'pickup':
      return {
        startTime: case_.pickupTimeStart,
        endTime: case_.pickupTimeEnd || case_.pickupTimeStart,
        label:
          case_.deliveryDate === selectedDate
            ? CASE_TYPE_LABELS.pickup_delivery
            : CASE_TYPE_LABELS.pickup,
      };
    case 'delivery':
      return {
        startTime: SCHEDULE_CONSTANTS.DEFAULT_DELIVERY_START_TIME,
        endTime: case_.deliveryTime,
        label: CASE_TYPE_LABELS.delivery,
      };
    case 'transit':
      return {
        startTime: SCHEDULE_CONSTANTS.DEFAULT_DELIVERY_START_TIME,
        endTime: SCHEDULE_CONSTANTS.DEFAULT_TRANSIT_END_TIME,
        label: CASE_TYPE_LABELS.transit,
      };
    default:
      return {
        startTime: case_.pickupTimeStart,
        endTime: case_.pickupTimeStart,
        label: CASE_TYPE_LABELS.unknown,
      };
  }
}

/**
 * 案件を時系列順でソート
 * @param cases 案件配列
 * @returns ソート済み案件配列
 */
export function sortCasesByTime(cases: Case[]): Case[] {
  return [...cases].sort((a, b) => {
    return a.pickupTimeStart.localeCompare(b.pickupTimeStart);
  });
}

/**
 * 指定日のスケジュール対象案件をフィルタリング
 * @param cases 案件配列
 * @param selectedDate 選択された日付（YYYY-MM-DD）
 * @returns フィルタリング済み案件配列
 */
export function filterScheduleCases(cases: Case[], selectedDate: string): Case[] {
  return cases.filter(case_ => {
    // 選択日が引き取り日の案件
    if (case_.pickupDate === selectedDate) {
      return true;
    }

    // 前日以前に引き取り開始し、選択日にまだ搬入完了していない案件
    if (case_.pickupDate < selectedDate && case_.deliveryDate >= selectedDate) {
      return true;
    }

    return false;
  });
}

/**
 * 案件の進行状況を分析
 * @param cases 案件配列
 * @returns 進行状況の統計情報
 */
export function analyzeScheduleProgress(cases: Case[]) {
  const total = cases.length;
  const completed = cases.filter(c => c.status === 'completed').length;
  const inTransit = cases.filter(c => c.status === 'in_transit').length;
  const assigned = cases.filter(c => c.status === 'assigned').length;

  return {
    total,
    completed,
    inTransit,
    assigned,
    completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
}
